import {
  Component,
  Input,
  ElementRef,
  HostListener,
  inject,
} from '@angular/core';

const ICON_PATHS: string[] = [
  /* 0 — GPS tracking */
  'M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
  /* 1 — Maintenance */
  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  /* 2 — Fuel */
  'M3 6l3 1m0 0l-3 9a5 5 0 006 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5 5 0 006 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3',
  /* 3 — Bidding */
  'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  /* 4 — Timeline */
  'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  /* 5 — Payments */
  'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
];

const GRADIENTS: [string,string][] = [
  ['rgba(0,140,240,0.22)','rgba(0,52,163,0.35)'],
  ['rgba(0,80,200,0.18)', 'rgba(0,140,240,0.28)'],
  ['rgba(0,140,240,0.16)','rgba(0,212,255,0.18)'],
  ['rgba(0,52,163,0.22)', 'rgba(0,140,240,0.32)'],
  ['rgba(0,212,255,0.14)','rgba(0,140,240,0.24)'],
  ['rgba(0,52,163,0.26)', 'rgba(0,212,255,0.16)'],
];

@Component({
  selector: 'app-info-card',
  standalone: true,
  imports: [],
  template: `
    <article
      class="glow-card glass rounded-2xl flex flex-col overflow-hidden h-full"
      style="border-color:var(--glass-border)"
    >
      <!-- Shimmer stripe (CSS hover-driven) -->
      <div class="shimmer-stripe"></div>

      <!-- ══ Custom SVG illustration header ═══ -->
      <div
        class="relative overflow-hidden shrink-0"
        style="height:160px"
        [style.background]="gradientBg"
      >
        <!-- Fine grid -->
        <div class="absolute inset-0" style="background-image:linear-gradient(rgba(255,255,255,0.045) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.045) 1px,transparent 1px);background-size:20px 20px"></div>

        @switch (index % 6) {

          <!-- 0 — GPS / Real-time Tracking -->
          @case (0) {
            <svg class="absolute inset-0 w-full h-full" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <!-- Map grid dots -->
              @for (row of [0,1,2,3]; track row) {
                @for (col of [0,1,2,3,4,5]; track col) {
                  <circle [attr.cx]="30 + col*36" [attr.cy]="25 + row*38" r="2" fill="rgba(0,140,240,0.22)"/>
                }
              }
              <!-- Dashed route line A→B -->
              <path d="M 38,135 Q 120,75 200,32" stroke="#008CF0" stroke-width="1.5" stroke-dasharray="7 4"/>
              <!-- Point A -->
              <circle cx="38" cy="135" r="4.5" fill="#008CF0" style="filter:drop-shadow(0 0 6px #008CF0)"/>
              <text x="46" y="139" fill="rgba(176,196,222,0.6)" font-size="9" font-family="Inter,sans-serif">Origin</text>
              <!-- Point B -->
              <circle cx="200" cy="32" r="4.5" fill="#00D4FF" style="filter:drop-shadow(0 0 6px #00D4FF)"/>
              <text x="180" y="28" fill="rgba(176,196,222,0.6)" font-size="9" font-family="Inter,sans-serif">Dest.</text>
              <!-- Current position pulse -->
              <circle cx="120" cy="82" r="22" fill="rgba(0,140,240,0.1)"/>
              <circle cx="120" cy="82" r="14" fill="none" stroke="#008CF0" stroke-width="1.2"
                style="animation:city-pulse-sm 2.2s ease-out infinite"/>
              <!-- Pin -->
              <path d="M120,69 C115,63 108,58 108,51 C108,44 113,38 120,38 C127,38 132,44 132,51 C132,58 125,63 120,69 Z"
                fill="#00D4FF" style="filter:drop-shadow(0 0 8px rgba(0,212,255,0.7))"/>
              <circle cx="120" cy="51" r="5" fill="rgba(5,12,21,0.75)"/>
            </svg>
          }

          <!-- 1 — Maintenance Scheduling -->
          @case (1) {
            <svg class="absolute inset-0 w-full h-full" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <!-- Calendar frame -->
              <rect x="30" y="18" width="130" height="110" rx="8" fill="none" stroke="rgba(0,140,240,0.35)" stroke-width="1.5"/>
              <rect x="30" y="18" width="130" height="26" rx="8" fill="rgba(0,140,240,0.2)"/>
              <line x1="30" y1="44" x2="160" y2="44" stroke="rgba(0,140,240,0.18)"/>
              <!-- Day labels -->
              @for (d of [48,76,104,132]; track d) {
                <text [attr.x]="d" y="56" fill="rgba(176,196,222,0.35)" font-size="7" font-family="Inter,sans-serif" text-anchor="middle">M</text>
              }
              <!-- Day cells -->
              @for (row of [0,1,2]; track row) {
                @for (col of [0,1,2,3]; track col) {
                  <circle [attr.cx]="48+col*28" [attr.cy]="74+row*26" r="10"
                    [attr.fill]="(row===1 && col===2) ? 'rgba(0,140,240,0.45)' : 'rgba(0,140,240,0.1)'"
                    [attr.stroke]="(row===1 && col===2) ? 'rgba(0,212,255,0.6)' : 'none'" stroke-width="1"/>
                  @if(row===1 && col===2){
                    <text x="104" y="79" fill="#00D4FF" font-size="9" text-anchor="middle" font-family="Inter,sans-serif">✓</text>
                  }
                }
              }
              <!-- Gear on right -->
              <g transform="translate(190,80)">
                <circle r="20" fill="rgba(5,12,21,0.8)" stroke="rgba(0,140,240,0.4)" stroke-width="1.5"/>
                <circle r="9"  fill="none" stroke="#008CF0" stroke-width="2"/>
                <line x1="0" y1="-20" x2="0" y2="-13" stroke="#008CF0" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="0" y1="13"  x2="0" y2="20"  stroke="#008CF0" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="-20" y1="0" x2="-13" y2="0" stroke="#008CF0" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="13"  y1="0" x2="20"  y2="0" stroke="#008CF0" stroke-width="2.5" stroke-linecap="round"/>
                <line x1="-14" y1="-14" x2="-9" y2="-9" stroke="#008CF0" stroke-width="2" stroke-linecap="round"/>
                <line x1="9"   y1="9"   x2="14" y2="14" stroke="#008CF0" stroke-width="2" stroke-linecap="round"/>
                <line x1="14"  y1="-14" x2="9" y2="-9"  stroke="#008CF0" stroke-width="2" stroke-linecap="round"/>
                <line x1="-9"  y1="9"   x2="-14" y2="14" stroke="#008CF0" stroke-width="2" stroke-linecap="round"/>
              </g>
            </svg>
          }

          <!-- 2 — Fuel Monitoring -->
          @case (2) {
            <svg class="absolute inset-0 w-full h-full" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <!-- Gauge arc background -->
              <path d="M 40,128 A 80,80 0 0 1 200,128" stroke="rgba(0,140,240,0.15)" stroke-width="16" stroke-linecap="round" fill="none"/>
              <!-- Gauge arc fill (75%) -->
              <path d="M 40,128 A 80,80 0 0 1 172,60" stroke="url(#fuelGrad)" stroke-width="16" stroke-linecap="round" fill="none"/>
              <defs>
                <linearGradient id="fuelGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"   stop-color="#008CF0"/>
                  <stop offset="75%"  stop-color="#00D4FF"/>
                  <stop offset="100%" stop-color="rgba(0,212,255,0.3)"/>
                </linearGradient>
              </defs>
              <!-- Tick marks -->
              @for (angle of [-90,-60,-30,0,30,60,90]; track angle) {
                <line
                  [attr.x1]="120 + Math.cos(angle * Math.PI/180) * 68"
                  [attr.y1]="128 + Math.sin(angle * Math.PI/180) * 68"
                  [attr.x2]="120 + Math.cos(angle * Math.PI/180) * 58"
                  [attr.y2]="128 + Math.sin(angle * Math.PI/180) * 58"
                  stroke="rgba(0,140,240,0.35)" stroke-width="1.5" stroke-linecap="round"
                />
              }
              <!-- Needle -->
              <line x1="120" y1="128" x2="172" y2="60" stroke="#00D4FF" stroke-width="2.5" stroke-linecap="round"
                style="filter:drop-shadow(0 0 6px #00D4FF)"/>
              <circle cx="120" cy="128" r="7" fill="rgba(5,12,21,0.9)" stroke="#00D4FF" stroke-width="2"/>
              <!-- Labels -->
              <text x="35" y="148" fill="rgba(176,196,222,0.5)" font-size="9" font-family="Inter,sans-serif">E</text>
              <text x="116" y="35" fill="rgba(176,196,222,0.5)" font-size="9" font-family="Inter,sans-serif">75%</text>
              <text x="200" y="148" fill="rgba(176,196,222,0.5)" font-size="9" font-family="Inter,sans-serif">F</text>
              <!-- Droplet icon right -->
              <path d="M205,30 C205,22 215,10 215,10 C215,10 225,22 225,30 C225,36 220,42 215,42 C210,42 205,36 205,30 Z"
                fill="rgba(0,140,240,0.35)" stroke="#008CF0" stroke-width="1.5"/>
            </svg>
          }

          <!-- 3 — Load Posting & Bidding -->
          @case (3) {
            <svg class="absolute inset-0 w-full h-full" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <!-- Shipper box -->
              <rect x="18" y="50" width="62" height="60" rx="8" fill="rgba(0,140,240,0.15)" stroke="rgba(0,140,240,0.4)" stroke-width="1.5"/>
              <!-- Box straps -->
              <line x1="49" y1="50" x2="49" y2="110" stroke="rgba(0,140,240,0.3)" stroke-width="1"/>
              <line x1="18" y1="80" x2="80" y2="80" stroke="rgba(0,140,240,0.3)" stroke-width="1"/>
              <text x="49" y="68" fill="rgba(176,196,222,0.55)" font-size="8" text-anchor="middle" font-family="Inter,sans-serif">LOAD</text>
              <!-- Carrier truck (simplified) -->
              <rect x="162" y="62" width="58" height="40" rx="4" fill="rgba(0,52,163,0.3)" stroke="rgba(0,140,240,0.5)" stroke-width="1.5"/>
              <rect x="140" y="74" width="25" height="28" rx="3" fill="rgba(0,140,240,0.25)" stroke="rgba(0,140,240,0.4)" stroke-width="1.2"/>
              <circle cx="160" cy="108" r="7" fill="rgba(5,12,21,0.8)" stroke="rgba(0,140,240,0.6)" stroke-width="1.5"/>
              <circle cx="207" cy="108" r="7" fill="rgba(5,12,21,0.8)" stroke="rgba(0,140,240,0.6)" stroke-width="1.5"/>
              <!-- Animated bid arrows -->
              <path d="M 88,70 L 132,65" stroke="#00D4FF" stroke-width="1.5" stroke-dasharray="6 3"
                style="animation:dash-flow 1.5s linear infinite" marker-end="url(#arr)"/>
              <path d="M 132,90 L 88,95" stroke="#008CF0" stroke-width="1.5" stroke-dasharray="6 3"
                style="animation:dash-flow 2s linear infinite reverse" marker-end="url(#arr2)"/>
              <defs>
                <marker id="arr"  markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#00D4FF"/>
                </marker>
                <marker id="arr2" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
                  <path d="M0,0 L6,3 L0,6 Z" fill="#008CF0"/>
                </marker>
              </defs>
              <!-- Bid tag -->
              <rect x="98" y="72" width="36" height="17" rx="4" fill="rgba(0,140,240,0.28)" stroke="rgba(0,212,255,0.4)" stroke-width="1"/>
              <text x="116" y="84" fill="#00D4FF" font-size="8.5" text-anchor="middle" font-family="Inter,sans-serif" font-weight="600">BID</text>
              <text x="49" y="130" fill="rgba(176,196,222,0.5)" font-size="8" text-anchor="middle" font-family="Inter,sans-serif">Shipper</text>
              <text x="186" y="130" fill="rgba(176,196,222,0.5)" font-size="8" text-anchor="middle" font-family="Inter,sans-serif">Carrier</text>
            </svg>
          }

          <!-- 4 — Real-time Delivery Timeline -->
          @case (4) {
            <svg class="absolute inset-0 w-full h-full" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <!-- Track line -->
              <line x1="28" y1="88" x2="212" y2="88" stroke="rgba(0,140,240,0.2)" stroke-width="3" stroke-linecap="round"/>
              <!-- Completed track fill -->
              <line x1="28" y1="88" x2="152" y2="88" stroke="#008CF0" stroke-width="3" stroke-linecap="round"
                style="filter:drop-shadow(0 0 5px rgba(0,140,240,0.6))"/>
              <!-- Milestone nodes -->
              @for (step of milestones; track step.x) {
                <circle [attr.cx]="step.x" cy="88" r="12"
                  [attr.fill]="step.done ? 'rgba(0,140,240,0.35)' : 'rgba(255,255,255,0.05)'"
                  [attr.stroke]="step.done ? '#008CF0' : 'rgba(255,255,255,0.12)'" stroke-width="1.5"/>
                @if(step.done){
                  <text [attr.x]="step.x" y="93" fill="#00D4FF" font-size="10" text-anchor="middle">✓</text>
                } @else if(step.current){
                  <circle [attr.cx]="step.x" cy="88" r="5" fill="#00D4FF"
                    style="animation:city-pulse 2s ease-out infinite"/>
                } @else {
                  <circle [attr.cx]="step.x" cy="88" r="4" fill="rgba(176,196,222,0.2)"/>
                }
                <text [attr.x]="step.x" y="114" fill="rgba(176,196,222,0.55)"
                  font-size="7.5" text-anchor="middle" font-family="Inter,sans-serif">{{ step.label }}</text>
              }
              <!-- Truck icon over current node -->
              <g transform="translate(148,55)">
                <rect x="-18" y="-10" width="36" height="20" rx="3" fill="rgba(0,52,163,0.5)" stroke="rgba(0,212,255,0.5)" stroke-width="1.2"/>
                <rect x="-30" y="-4" width="14" height="14" rx="2" fill="rgba(0,140,240,0.4)" stroke="rgba(0,140,240,0.5)" stroke-width="1"/>
                <circle cx="-15" cy="12" r="4" fill="rgba(5,12,21,0.8)" stroke="#008CF0" stroke-width="1.2"/>
                <circle cx="14"  cy="12" r="4" fill="rgba(5,12,21,0.8)" stroke="#008CF0" stroke-width="1.2"/>
              </g>
            </svg>
          }

          <!-- 5 — Payments & Ratings -->
          @case (5) {
            <svg class="absolute inset-0 w-full h-full" viewBox="0 0 240 160" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <!-- Payment card -->
              <rect x="20" y="25" width="130" height="80" rx="10" fill="rgba(0,52,163,0.25)" stroke="rgba(0,140,240,0.45)" stroke-width="1.5"/>
              <rect x="20" y="45" width="130" height="18" fill="rgba(0,140,240,0.15)"/>
              <!-- Chip -->
              <rect x="32" y="30" width="22" height="16" rx="3" fill="rgba(0,212,255,0.25)" stroke="rgba(0,212,255,0.4)" stroke-width="1"/>
              <!-- Card number dots -->
              @for (g of [0,1,2,3]; track g) {
                @for (d of [0,1,2,3]; track d) {
                  <circle [attr.cx]="32 + g*28 + d*5" cy="76" r="2.2" fill="rgba(176,196,222,0.4)"/>
                }
              }
              <!-- Currency labels -->
              <text x="30"  y="97" fill="rgba(0,212,255,0.6)" font-size="9" font-family="Inter,sans-serif" font-weight="600">RWF</text>
              <text x="75"  y="97" fill="rgba(176,196,222,0.45)" font-size="8" font-family="Inter,sans-serif">USD</text>
              <text x="110" y="97" fill="rgba(176,196,222,0.45)" font-size="8" font-family="Inter,sans-serif">EUR</text>
              <!-- Stars -->
              @for (s of [0,1,2,3,4]; track s) {
                <text [attr.x]="28 + s*28" y="135"
                  [attr.fill]="s < 4 ? '#008CF0' : 'rgba(176,196,222,0.2)'"
                  font-size="18" font-family="Arial,sans-serif"
                  style="filter:drop-shadow(0 0 4px rgba(0,140,240,0.6))">★</text>
              }
              <!-- Rating value -->
              <text x="168" y="132" fill="#00D4FF" font-size="18" font-family="Inter,sans-serif" font-weight="700">4.8</text>
              <!-- Trend arrow -->
              <path d="M192,40 L208,20 L215,30" stroke="#00D4FF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="192" y1="40" x2="215" y2="30" stroke="#00D4FF" stroke-width="2" stroke-linecap="round"/>
            </svg>
          }
        }

        <!-- Small icon badge (bottom-left) -->
        <div
          class="absolute bottom-3 left-4 flex items-center justify-center rounded-xl"
          style="width:2.25rem;height:2.25rem;background:rgba(0,140,240,0.22);border:1px solid rgba(0,140,240,0.45)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" style="width:1.1rem;height:1.1rem;color:#00D4FF" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPath"/>
          </svg>
        </div>
      </div>

      <!-- ══ Card content ═══════════════════════ -->
      <div class="relative flex flex-col flex-1 px-5 py-4 gap-2" style="z-index:5">
        <h3 style="font-size:0.68rem;letter-spacing:0.16em;text-transform:uppercase;color:#008CF0;font-family:'Space Grotesk',sans-serif;font-weight:700">
          {{ title }}
        </h3>
        <p class="text-sm leading-relaxed" style="color:var(--theme-text-dim)">{{ description }}</p>
      </div>
    </article>
  `,
})
export class InfoCardComponent {
  @Input() title = '';
  @Input() description = '';
  @Input() imageSrc = '';
  @Input() imageAlt = '';
  @Input() featured = false;
  @Input() index = 0;

  private el = inject(ElementRef);

  /** Milestone data for timeline card */
  readonly milestones = [
    { x: 28,  label: 'Posted',   done: true,  current: false },
    { x: 88,  label: 'Quoted',   done: true,  current: false },
    { x: 152, label: 'Moving',   done: false, current: true  },
    { x: 212, label: 'Delivered',done: false, current: false },
  ];

  /** Math reference for SVG fuel gauge tick marks */
  readonly Math = Math;

  get iconPath(): string  { return ICON_PATHS[this.index % ICON_PATHS.length]; }
  get gradientBg(): string {
    const [a, b] = GRADIENTS[this.index % GRADIENTS.length];
    return `linear-gradient(135deg,${a} 0%,${b} 100%)`;
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(e: MouseEvent): void {
    const el = this.el.nativeElement as HTMLElement;
    const rect = el.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top)  / rect.height;
    el.style.setProperty('--mouse-x', `${nx * 100}%`);
    el.style.setProperty('--mouse-y', `${ny * 100}%`);
    el.style.transform = `perspective(900px) rotateX(${(ny - 0.5) * -9}deg) rotateY(${(nx - 0.5) * 9}deg) translateY(-5px) scale(1.015)`;
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    const el = this.el.nativeElement as HTMLElement;
    el.style.transform = '';
    el.style.removeProperty('--mouse-x');
    el.style.removeProperty('--mouse-y');
  }
}
